import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IonInput, IonText } from '@ionic/react';
import { RootState } from 'core/store';
import type { InventoryItem, ProductModelSearchResult } from 'core/types';
import { saveProductUpdate } from 'features/shared/inventory/utils/saveProductUpdate';
import { buildProductEditPatchBody } from 'features/shared/inventory/utils/buildProductEditPatchBody';
import { displaySuccess } from 'core/services/AlertActions';
import ProductModelSearchSelect from 'features/shared/inventory/components/ProductModelSearchSelect';
import HKSelect from 'components/hk-select/HKSelect';
import {
  NA_MANUFACTURER_ID,
  useProductCatalogEditFields
} from 'features/shared/inventory/hooks/useProductCatalogEditFields';
import './InventoryProductEditForm.scss';

interface InventoryProductEditFormProps {
  product: InventoryItem;
  onSaved: () => void;
}

export interface InventoryProductEditFormHandle {
  save: () => void;
}

/**
 * Edit-mode content for the item detail (mobile modal + desktop route).
 * Per design.md D1 (updated): Group, Product Type, and Brand/Manufacturer are
 * cascading closed selects (each change clears every field below it); Model #
 * stays a searchable typeahead scoped by the chosen Type+Brand; Serial # is
 * the only free-text field. Registered is not homeowner-editable here. Save
 * issues `PATCH /home/{home_id}/product/{id}` with only the final selected
 * `product_template_id` + `serial` — Group/Type/Brand are catalog-browsing
 * aids to find that id, not independently-submitted fields.
 *
 * Cancel/Save live in the mobile page's own footer IonToolbar (Figma node
 * 8699:7036's sticky bottom bar), not inside this component — `save` is
 * exposed via `ref` (useImperativeHandle) so the parent's footer button can
 * trigger it without lifting all of this form's internal state up.
 */
const InventoryProductEditForm = forwardRef<
  InventoryProductEditFormHandle,
  InventoryProductEditFormProps
>(({ product, onSaved }, ref) => {
  const dispatch = useDispatch();
  const homeId = useSelector((state: RootState) => state.home.currentHome?.id);
  const { updateProductLoading } = useSelector(
    (state: RootState) => state.inventory
  );
  const [serial, setSerial] = useState(product.serial || '');
  const {
    initialize,
    groupId,
    setGroupId,
    groupOptions,
    typeId,
    setTypeId,
    typeOptions,
    manufacturerId,
    setManufacturerId,
    manufacturerOptions,
    model,
    setModel
  } = useProductCatalogEditFields();

  // Runs once on mount only — `initialize` is an explicit imperative reset,
  // not a value the form should re-sync to on every `product` prop update
  // (see useProductCatalogEditFields.ts's initialize() doc comment).
  useEffect(() => {
    initialize(product);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelectModel = (result: ProductModelSearchResult) => {
    setModel({
      id: result.id,
      label: result.model
    });
  };

  const save = async () => {
    if (!homeId) return;
    const body = buildProductEditPatchBody(
      product,
      serial,
      model,
      manufacturerId ?? null
    );
    const { ok, changed } = await saveProductUpdate(
      dispatch,
      homeId,
      product.id,
      body
    );
    // Stay in edit mode on failure so the user's edits survive to be retried —
    // createApiRequest has already shown the real error message.
    if (!ok) return;
    if (changed) {
      dispatch(displaySuccess('Product Updated', 'Success'));
    }
    onSaved();
  };

  useImperativeHandle(ref, () => ({ save }));

  return (
    <div className="hk-inventory-product-edit-form">
      <HKSelect
        label="Group"
        value={groupId}
        options={groupOptions}
        onChange={(value) => setGroupId(value as number | null)}
      />
      <HKSelect
        label="Product Type"
        value={typeId}
        options={typeOptions}
        disabled={!groupId}
        onChange={(value) => setTypeId(value as number | null)}
      />
      <HKSelect
        label="Brand/Manufacturer"
        value={manufacturerId}
        options={manufacturerOptions}
        disabled={!typeId}
        onChange={(value) => setManufacturerId(value as number | null)}
      />

      <div className="hk-inventory-edit-field">
        <IonText color="medium">Model #</IonText>
        <ProductModelSearchSelect
          value={model?.id}
          valueLabel={model?.label}
          disabled={!manufacturerId || manufacturerId === NA_MANUFACTURER_ID}
          typeId={typeId}
          manufacturerId={manufacturerId}
          onChange={onSelectModel}
        />
      </div>

      <div className="hk-inventory-edit-field">
        <IonText color="medium">Serial #</IonText>
        <IonInput
          className="hk-inventory-edit-input"
          fill="outline"
          value={serial}
          placeholder="Enter serial number"
          disabled={updateProductLoading}
          onIonInput={(e) => setSerial(e.detail.value || '')}
        />
      </div>
    </div>
  );
});

InventoryProductEditForm.displayName = 'InventoryProductEditForm';

export default InventoryProductEditForm;
